import { createAppRequest } from '@/services/request';
import type {
  DecisionCondition,
  DecisionMinutes,
  DiligenceReport,
  ClosingCondition,
  DecisionListQuery,
  DecisionStatus,
  DueDiligenceItem,
  Fund,
  InvestmentDashboard,
  InvestmentApproval,
  InvestmentApprovalStatus,
  InvestmentApprovalType,
  InvestmentDecision,
  InvestmentMaterialItem,
  InvestmentProject,
  LifecycleReadiness,
  Lead,
  LeadListQuery,
  LeadListResult,
  LeadStatus,
  ProjectListQuery,
  ProjectFiling,
  ProjectStage,
  RiskItem,
  SetDecisionResultPayload,
  SubmitDecisionPayload,
  Transaction,
  TransactionAmendment,
  TransactionAgreement,
  TransactionPayment,
  TransactionListQuery,
  UpdateFundPayload,
  UpdateProjectPayload,
  UpdateTransactionProgressPayload,
} from '@/features/investment/types';
import type { FollowUp } from '@/features/investment/types';
import type {
  LeadScreening,
  LeadScreeningOpinion,
  LeadScreeningRejectionReason,
} from '@/features/investment/types';

const request = createAppRequest();

/** Display-only: never send snapshot/legacy name fields as write identity. */
const withProjectFundSnapshot = (project: InvestmentProject): InvestmentProject => ({
  ...project,
  company: project.companyNameSnapshot || project.company || '',
  targetFund: project.fundNameSnapshot || project.targetFund || '',
});

const withDecisionFundSnapshot = (decision: InvestmentDecision): InvestmentDecision => ({
  ...decision,
  targetFund: decision.fundNameSnapshot || decision.targetFund || '',
});

const withTransactionFundSnapshot = (transaction: Transaction): Transaction => ({
  ...transaction,
  fund: transaction.fundNameSnapshot || transaction.fund || '',
});

const toUpdateProjectBody = (data: UpdateProjectPayload) => {
  const { targetFund: _legacyFund, company: _legacyCompany, ...body } = data as UpdateProjectPayload & {
    targetFund?: string;
    company?: string;
  };
  return Object.fromEntries(
    Object.entries({
      ...body,
      fundUuid: data.fundUuid,
      companyOrganizationUuid: data.companyOrganizationUuid,
    }).filter(([, value]) => value !== undefined),
  );
};

const toCreateLeadBody = (data: Partial<Lead>) => {
  const body = {
    company: data.company,
    primaryOrganizationUuid: data.primaryOrganizationUuid,
    referrerPersonUuid: data.referrerPersonUuid,
    industry: data.industry,
    round: data.round,
    financingAmount: data.financingAmount,
    preMoneyValuation: data.preMoneyValuation,
    source: data.source,
    referrer: data.referrer,
    owner: data.owner,
    ownerMemberUuid: data.ownerMemberUuid,
    region: data.region,
    summary: data.summary,
    highlight: data.highlight,
    initialJudgment: data.initialJudgment,
  };
  return Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined && value !== ''),
  );
};

export const toUpdateLeadBody = (data: Partial<Lead>) => {
  return Object.fromEntries(
    Object.entries({
      name: data.name,
      company: data.company,
      primaryOrganizationUuid: data.primaryOrganizationUuid,
      referrerPersonUuid: data.referrerPersonUuid,
      industry: data.industry,
      round: data.round,
      financingAmount: data.financingAmount,
      preMoneyValuation: data.preMoneyValuation,
      source: data.source,
      referrer: data.referrer,
      owner: data.owner,
      ownerMemberUuid: data.ownerMemberUuid,
      region: data.region,
      summary: data.summary,
      nextAction: data.nextAction,
      highlight: data.highlight,
      initialJudgment: data.initialJudgment,
    }).filter(([, value]) => value !== undefined),
  );
};

export const investmentApi = {
  listFunds: (includeInactive = false) =>
    request<Fund[]>('/bff/investment/funds', {
      params: includeInactive ? { includeInactive: true } : undefined,
    }),

  createFund: (data: Pick<Fund, 'code' | 'name' | 'currency'>) =>
    request<Fund>('/bff/investment/funds', {
      method: 'POST',
      data,
    }),

  updateFund: (uuid: string, data: UpdateFundPayload) =>
    request<Fund>(`/bff/investment/funds/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listApprovals: (params: { bizType: string; bizUuid: string }) =>
    request<InvestmentApproval[]>('/bff/investment/governance/approvals', {
      params,
    }),

  createApproval: (data: {
    approvalType: InvestmentApprovalType;
    bizType: string;
    bizUuid: string;
  }) =>
    request<InvestmentApproval>('/bff/investment/governance/approvals', {
      method: 'POST',
      data,
    }),

  completeApproval: (
    uuid: string,
    data: {
      status: Extract<InvestmentApprovalStatus, '已通过' | '已驳回'>;
      opinion?: string;
      adminExceptionReason?: string;
    },
  ) =>
    request<InvestmentApproval>(`/bff/investment/governance/approvals/${uuid}/complete`, {
      method: 'PATCH',
      data,
    }),

  withdrawApproval: (uuid: string) =>
    request<InvestmentApproval>(`/bff/investment/governance/approvals/${uuid}/withdraw`, {
      method: 'POST',
    }),

  listMaterials: (params: { bizType: string; bizUuid: string }) =>
    request<InvestmentMaterialItem[]>('/bff/investment/governance/materials', {
      params,
    }),

  createMaterial: (
    bizType: string,
    bizUuid: string,
    data: { materialCode: string; title: string; required?: boolean },
  ) =>
    request<InvestmentMaterialItem>(
      `/bff/investment/governance/materials/${bizType}/${bizUuid}`,
      { method: 'POST', data },
    ),

  verifyMaterial: (uuid: string, data: { verified?: boolean; waiverReason?: string }) =>
    request<InvestmentMaterialItem>(`/bff/investment/governance/materials/${uuid}/verify`, {
      method: 'PATCH',
      data,
    }),

  linkMaterialAttachment: (uuid: string, attachmentUuid: string) =>
    request<{ uuid: string }>(`/bff/investment/governance/materials/${uuid}/attachments`, {
      method: 'POST',
      data: { attachmentUuid },
    }),

  listLeads: (params?: LeadListQuery) =>
    request<LeadListResult>('/bff/investment/leads', { params }),

  getLead: (uuid: string) => request<Lead>(`/bff/investment/leads/${uuid}`),

  createLead: (data: Partial<Lead>) =>
    request<Lead>('/bff/investment/leads', {
      method: 'POST',
      data: toCreateLeadBody(data),
    }),

  updateLead: (uuid: string, data: Partial<Lead>) =>
    request<Lead>(`/bff/investment/leads/${uuid}`, {
      method: 'PATCH',
      data: toUpdateLeadBody(data),
    }),

  updateLeadStatus: (uuid: string, status: LeadStatus) =>
    request<Lead>(`/bff/investment/leads/${uuid}/status`, {
      method: 'PATCH',
      data: { status },
    }),

  resumeLead: (uuid: string) =>
    request<Lead>(`/bff/investment/leads/${uuid}/resume`, { method: 'POST' }),

  claimLead: (uuid: string, data?: { owner?: string }) =>
    request<Lead>(`/bff/investment/leads/${uuid}/claim`, {
      method: 'POST',
      data: data ?? {},
    }),

  deleteLead: (uuid: string) =>
    request<{ message: string }>(`/bff/investment/leads/${uuid}`, {
      method: 'DELETE',
    }),

  listLeadFollowUps: (uuid: string) =>
    request<FollowUp[]>(`/bff/investment/leads/${uuid}/follow-ups`),

  createLeadFollowUp: (uuid: string, data: { content: string; actor?: string }) =>
    request<FollowUp>(`/bff/investment/leads/${uuid}/follow-ups`, {
      method: 'POST',
      data,
    }),

  listLeadScreenings: (uuid: string) =>
    request<LeadScreening[]>(`/bff/investment/leads/${uuid}/screenings`),

  createLeadScreening: (
    uuid: string,
    data: {
      opinion: LeadScreeningOpinion;
      rejectionReason?: LeadScreeningRejectionReason;
      note?: string;
    },
  ) =>
    request<LeadScreening>(`/bff/investment/leads/${uuid}/screenings`, {
      method: 'POST',
      data,
    }),

  convertLead: (uuid: string) =>
    request<InvestmentProject>(`/bff/investment/leads/${uuid}/convert`, {
      method: 'POST',
      headers: { 'Idempotency-Key': `lead:${uuid}:convert` },
    }).then(withProjectFundSnapshot),

  listProjects: (params?: ProjectListQuery) =>
    request<InvestmentProject[]>('/bff/investment/projects', { params }).then(
      (items) => (items ?? []).map(withProjectFundSnapshot),
    ),

  getProject: (uuid: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}`).then(
      withProjectFundSnapshot,
    ),

  getDecisionReadiness: (uuid: string) =>
    request<LifecycleReadiness>(`/bff/investment/projects/${uuid}/decision-readiness`),

  updateProject: (uuid: string, data: UpdateProjectPayload) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}`, {
      method: 'PATCH',
      data: toUpdateProjectBody(data),
    }).then(withProjectFundSnapshot),

  updateProjectStage: (uuid: string, stage: ProjectStage) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/stage`, {
      method: 'PATCH',
      data: { stage },
    }).then(withProjectFundSnapshot),

  resumeProject: (uuid: string) =>
    request<InvestmentProject>(`/bff/investment/projects/${uuid}/resume`, {
      method: 'POST',
    }).then(withProjectFundSnapshot),

  submitDecision: (projectUuid: string, data?: SubmitDecisionPayload) =>
    request<InvestmentDecision>(
      `/bff/investment/projects/${projectUuid}/submit-decision`,
      { method: 'POST', data: data ?? {} },
    ).then(withDecisionFundSnapshot),

  listDecisions: (params?: DecisionListQuery) =>
    request<InvestmentDecision[]>('/bff/investment/decisions', { params }).then(
      (items) => (items ?? []).map(withDecisionFundSnapshot),
    ),

  getDecision: (uuid: string) =>
    request<InvestmentDecision>(`/bff/investment/decisions/${uuid}`).then(
      withDecisionFundSnapshot,
    ),

  setDecisionResult: (uuid: string, data: SetDecisionResultPayload) =>
    request<InvestmentDecision>(`/bff/investment/decisions/${uuid}/result`, {
      method: 'PATCH',
      data,
    }).then(withDecisionFundSnapshot),

  listTransactions: (params?: TransactionListQuery) =>
    request<Transaction[]>('/bff/investment/transactions', { params }).then(
      (items) => (items ?? []).map(withTransactionFundSnapshot),
    ),

  getTransaction: (uuid: string) =>
    request<Transaction>(`/bff/investment/transactions/${uuid}`).then(
      withTransactionFundSnapshot,
    ),

  getClosingReadiness: (uuid: string) =>
    request<LifecycleReadiness>(`/bff/investment/transactions/${uuid}/closing-readiness`),

  completeClosing: (
    uuid: string,
    payload?: { finalInvestmentAmount?: number; cashWaiver?: { reason: string } },
  ) =>
    request<{
      transaction: Transaction;
      handoff?: { companyUuid?: string; investmentRelationUuid?: string; companyName?: string };
    }>(
      `/bff/investment/transactions/${uuid}/complete-closing`,
      { method: 'POST', data: payload ?? {} },
    ).then((result) => ({
      ...result,
      transaction: withTransactionFundSnapshot(result.transaction),
    })),

  getDashboard: (params?: {
    manager?: string;
    industry?: string;
    source?: string;
    fund?: string;
  }) =>
    request<InvestmentDashboard>('/bff/investment/dashboard', { params }),

  updateTransactionProgress: (
    uuid: string,
    data: UpdateTransactionProgressPayload,
  ) =>
    request<Transaction>(`/bff/investment/transactions/${uuid}/progress`, {
      method: 'PATCH',
      data,
    }).then(withTransactionFundSnapshot),

  listDiligenceItems: (projectUuid: string) =>
    request<DueDiligenceItem[]>(
      `/bff/investment/projects/${projectUuid}/diligence-items`,
    ),

  createDiligenceItem: (
    projectUuid: string,
    data: Omit<DueDiligenceItem, 'id' | 'projectId'>,
  ) =>
    request<DueDiligenceItem>(
      `/bff/investment/projects/${projectUuid}/diligence-items`,
      { method: 'POST', data },
    ),

  updateDiligenceItem: (uuid: string, data: Partial<DueDiligenceItem>) =>
    request<DueDiligenceItem>(`/bff/investment/diligence-items/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listProjectRiskItems: (projectUuid: string) =>
    request<RiskItem[]>(`/bff/investment/projects/${projectUuid}/risk-items`),

  createProjectRiskItem: (
    projectUuid: string,
    data: Omit<RiskItem, 'id' | 'projectId'>,
  ) =>
    request<RiskItem>(`/bff/investment/projects/${projectUuid}/risk-items`, {
      method: 'POST',
      data,
    }),

  updateProjectRiskItem: (uuid: string, data: Partial<RiskItem>) =>
    request<RiskItem>(`/bff/investment/risk-items/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listDecisionConditions: (decisionUuid: string) =>
    request<DecisionCondition[]>(
      `/bff/investment/decisions/${decisionUuid}/conditions`,
    ),

  createDecisionCondition: (
    decisionUuid: string,
    data: {
      title: string;
      owner?: string;
      dueDate?: string;
      required?: boolean;
    },
  ) =>
    request<DecisionCondition>(
      `/bff/investment/decisions/${decisionUuid}/conditions`,
      {
        method: 'POST',
        data: {
          title: data.title,
          owner: data.owner,
          dueDate: data.dueDate,
          required: data.required,
        },
      },
    ),

  updateDecisionCondition: (uuid: string, data: Partial<DecisionCondition>) =>
    request<DecisionCondition>(`/bff/investment/decision-conditions/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listTransactionAmendments: (transactionUuid: string) =>
    request<TransactionAmendment[]>(
      `/bff/investment/transactions/${transactionUuid}/amendments`,
    ),

  createTransactionAmendment: (
    transactionUuid: string,
    data: {
      type: TransactionAmendment['type'];
      title: string;
      summary?: string;
      signedAt?: string;
      owner?: string;
      affectsAmount?: boolean;
      affectsHolding?: boolean;
      amountAfter?: number;
      holdingAfter?: number;
    },
  ) =>
    request<TransactionAmendment>(
      `/bff/investment/transactions/${transactionUuid}/amendments`,
      { method: 'POST', data },
    ),

  updateTransactionAmendment: (
    uuid: string,
    data: Partial<TransactionAmendment>,
  ) =>
    request<TransactionAmendment>(
      `/bff/investment/transaction-amendments/${uuid}`,
      { method: 'PATCH', data },
    ),

  listProjectFilings: (projectUuid: string) =>
    request<ProjectFiling[]>(`/bff/investment/projects/${projectUuid}/filings`),
  createProjectFiling: (
    projectUuid: string,
    data: Pick<ProjectFiling, 'investmentAmount' | 'holdingRatio' | 'progress'>,
  ) => request<ProjectFiling>(`/bff/investment/projects/${projectUuid}/filings`, { method: 'POST', data }),
  updateProjectFiling: (uuid: string, data: Partial<ProjectFiling>) =>
    request<ProjectFiling>(`/bff/investment/project-filings/${uuid}`, { method: 'PATCH', data }),

  listDiligenceReports: (projectUuid: string) =>
    request<DiligenceReport[]>(`/bff/investment/projects/${projectUuid}/diligence-reports`),
  createDiligenceReport: (projectUuid: string, data: Partial<DiligenceReport>) =>
    request<DiligenceReport>(`/bff/investment/projects/${projectUuid}/diligence-reports`, { method: 'POST', data }),
  updateDiligenceReport: (uuid: string, data: Partial<DiligenceReport>) =>
    request<DiligenceReport>(`/bff/investment/diligence-reports/${uuid}`, { method: 'PATCH', data }),

  listDecisionMinutes: (decisionUuid: string) =>
    request<DecisionMinutes[]>(`/bff/investment/decisions/${decisionUuid}/minutes`),
  createDecisionMinutes: (decisionUuid: string, data: Partial<DecisionMinutes>) =>
    request<DecisionMinutes>(`/bff/investment/decisions/${decisionUuid}/minutes`, { method: 'POST', data }),
  updateDecisionMinutes: (uuid: string, data: Partial<DecisionMinutes>) =>
    request<DecisionMinutes>(`/bff/investment/decision-minutes/${uuid}`, { method: 'PATCH', data }),

  listTransactionAgreements: (transactionUuid: string) =>
    request<TransactionAgreement[]>(`/bff/investment/transactions/${transactionUuid}/agreements`),
  createTransactionAgreement: (transactionUuid: string, data: Partial<TransactionAgreement>) =>
    request<TransactionAgreement>(`/bff/investment/transactions/${transactionUuid}/agreements`, { method: 'POST', data }),
  updateTransactionAgreement: (uuid: string, data: Partial<TransactionAgreement>) =>
    request<TransactionAgreement>(`/bff/investment/transaction-agreements/${uuid}`, { method: 'PATCH', data }),

  listTransactionPayments: (transactionUuid: string) =>
    request<TransactionPayment[]>(`/bff/investment/transactions/${transactionUuid}/payments`),
  createTransactionPayment: (transactionUuid: string, data: Partial<TransactionPayment>) =>
    request<TransactionPayment>(`/bff/investment/transactions/${transactionUuid}/payments`, { method: 'POST', data }),
  updateTransactionPayment: (uuid: string, data: Partial<TransactionPayment>) =>
    request<TransactionPayment>(`/bff/investment/transaction-payments/${uuid}`, { method: 'PATCH', data }),

  listClosingConditions: (transactionUuid: string) =>
    request<ClosingCondition[]>(`/bff/investment/transactions/${transactionUuid}/closing-conditions`),
  createClosingCondition: (transactionUuid: string, data: Partial<ClosingCondition>) =>
    request<ClosingCondition>(`/bff/investment/transactions/${transactionUuid}/closing-conditions`, { method: 'POST', data }),
  updateClosingCondition: (uuid: string, data: Partial<ClosingCondition>) =>
    request<ClosingCondition>(`/bff/investment/closing-conditions/${uuid}`, { method: 'PATCH', data }),
};
